import {Queries} from '../../api/queries';
import {formatCurrency} from '../../formatters';
import {gql} from '../../graphql';
import {getInvoiceAmountDue, getInvoiceTotal} from '../../invoice-helpers';
import {List} from '../List';
import MobileFriendlyItemDisplay from '../MobileFriendlyItemDisplay';
import Table from '../Table';
import DollarAmount from '../visual/DollarAmount';
import {Button, Icon, Link, Text} from '@sproutsocial/racine';
import {useQuery} from '@tanstack/react-query';
import {DateTime} from 'luxon';
import React from 'react';
import {keyBy} from 'shared/src/map';
import type {Invoice} from 'shared/src/models';

interface InvoiceTableProps {
	invoices: Invoice[];
	showCustomerName?: boolean;
}

const InvoiceTable = ({invoices, showCustomerName = true}: InvoiceTableProps) => {
	const {data: customers} = useQuery(Queries.customers());

	const customersById = keyBy(customers ?? [], (customer) => customer.id);

	return (
		<MobileFriendlyItemDisplay
			list={
				<List id='all-invoices'>
					{invoices.map((invoice) => {
						const amountDue = getInvoiceAmountDue(invoice.items);

						return (
							<List.Item
								key={invoice.id}
								title={showCustomerName ? customersById.get(invoice.customer)?.name : undefined}
								description={invoice.date.toLocaleString(DateTime.DATE_MED)}
								data={formatCurrency(getInvoiceTotal(invoice.items))}
								subdata={
									<Text fontSize='200' color={amountDue === 0 ? 'green.800' : 'red.800'}>
										{amountDue === 0 ? 'PAID' : formatCurrency(amountDue)}
									</Text>
								}
							/>
						);
					})}
				</List>
			}
			table={
				<Table
					id='all-invoices'
					head={[
						{id: 'id', content: 'ID'},
						{id: 'date', content: 'Date'},
						{id: 'cost', content: 'Cost'},
						{id: 'due', content: 'Due'},
						...(showCustomerName ? [{id: 'customer', content: 'Customer Name'}] : []),
						{id: 'edit', content: ''},
						{id: 'preview', content: ''},
						{id: 'download', content: ''},
					]}
					items={invoices}
					generateRow={(invoice) => {
						const amountDue = getInvoiceAmountDue(invoice.items);

						return {
							id: String(invoice.id),
							cells: [
								<Link href={`/preview/${invoice.id}`} external>
									{invoice.publicId.toString().padStart(4, '0')}
								</Link>,
								invoice.date.toLocaleString(DateTime.DATE_MED),
								formatCurrency(getInvoiceTotal(invoice.items)),
								<DollarAmount fontWeight='semibold' value={amountDue} isBalanceDue />,
								...(showCustomerName
									? [
											<Link href={`/customers/${invoice.customer}/invoices`}>
												{customersById.get(invoice.customer)?.name}
											</Link>,
										]
									: []),
								<Link href={`/invoices/${invoice.id}/edit`}>
									<Icon name='pencil' />
								</Link>,
								<Link href={`/preview/${invoice.id}`} external>
									<Icon name='eye' />
								</Link>,
								<form
									method='POST'
									action={`/api/invoices/pdf/${invoice.id}`}
									style={{display: 'inline'}}
								>
									<Button
										// @ts-ignore
										type='submit'
										p={0}
									>
										<Icon
											name='arrow-down-to-bracket-solid'
											color='button.primary.background.base'
										/>
									</Button>
								</form>,
							],
						};
					}}
				/>
			}
		/>
	);
};

export const InvoiceTableFields = gql(/* GraphQL */ `
	fragment InvoiceTableFields on Invoice {
		id
		publicId
		date
		customer {
			id
			name
		}
		items {
			type
			quantity
			price
			taxed
		}
	}
`);

export default InvoiceTable;
